import { Component, type ErrorInfo, type ReactNode } from 'react';
import { log } from '@/lib/logger';
import { AppLoadingShell } from './AppLoadingShell';

interface AppErrorBoundaryProps {
  children: ReactNode;
  /** Injected for tests; defaults to a full page reload. */
  onReload?: () => void;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

/**
 * Root error boundary. Without it, any render/commit-phase error unmounts
 * the entire React tree and leaves a permanent white screen - a brand-new
 * user hit exactly that on 2026-07-14 when Chrome Translate broke a route
 * transition (see the shared translateDomGuards module for the crash class).
 */
export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // A boundary-caught error is no longer "unhandled", so PostHog's
    // exception autocapture misses it; log.error re-mints it via
    // console-error autocapture (same channel as main.tsx network failures).
    log.error('[AppErrorBoundary] Render crash caught', {
      error: `${error.name}: ${error.message}`,
      componentStack: errorInfo.componentStack ?? undefined,
    });
  }

  private handleReload = (): void => {
    if (this.props.onReload) {
      this.props.onReload();
      return;
    }
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <AppLoadingShell>
        <section className="card-sm max-w-xl w-full" role="alert">
          <h2 className="text-base font-semibold text-gray-900">
            Something went wrong
          </h2>
          <p className="mt-2 text-sm text-gray-600 [text-wrap:balance]">
            The page ran into an unexpected&nbsp;error.
          </p>
          <button type="button" className="btn-primary mt-4" onClick={this.handleReload}>
            <span className="text-cap-trim">Reload page</span>
          </button>
        </section>
      </AppLoadingShell>
    );
  }
}
